import hammer from '@/assets/brand/recepthor-hammer-256.png';
import { DropdownMenu } from '@/ui';
import {
  AssignmentOutlinedIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DescriptionIcon,
  GavelOutlinedIcon,
  GearIcon,
  HelpOutlineIcon,
  HomeOutlinedIcon,
  InboxOutlinedIcon,
  PaymentsOutlinedIcon,
  SwapIcon,
} from '@/ui/icons';
import { useEffect, useRef, useState } from 'react';
import './Sidebar.css';

// Nav items carry a single `shortcut` letter; navigation fires with the
// "g then <letter>" chord (handled globally in MainLayout). Order reflects
// the operational flow: causa → diligencia → pago.
// Nav model: Operación · Finanzas · Directorio ·
// Sistema. Items carry a single `shortcut` letter (the "g <letter>" chord). The
// `perm` field gates an item: `resolveNavGroups` drops it unless the flag it
// names is granted ('billing' for billing:read, 'payout:manual' as is).
export const NAV_GROUPS = [
  {
    label: 'Operación',
    short: 'OPS',
    items: [
      { label: 'Inicio', path: '/', shortcut: 'I', Icon: HomeOutlinedIcon },
      {
        label: 'Causas',
        path: '/causas',
        shortcut: 'C',
        Icon: GavelOutlinedIcon,
      },
      {
        label: 'Diligencias',
        path: '/diligencias',
        shortcut: 'D',
        Icon: AssignmentOutlinedIcon,
      },
    ],
  },
  {
    label: 'Finanzas',
    short: 'FIN',
    items: [
      {
        label: 'Pagos',
        path: '/pagos',
        shortcut: 'P',
        Icon: PaymentsOutlinedIcon,
      },
      {
        label: 'Facturación',
        path: '/facturacion',
        Icon: DescriptionIcon,
        perm: 'billing',
      },
    ],
  },
  {
    label: 'Sistema',
    short: 'SYS',
    items: [{ label: 'Ayuda', path: '/ayuda', Icon: HelpOutlineIcon }],
  },
  // Cuenta + Organización are NOT in the rail — they live in the account menu
  // (sidebar footer, the placement): "Configuración" → /sistema/cuenta,
  // "Ver estudio" → /sistema/organizacion.
];

// Platform (backoffice) nav. WhatsApp is the daily JT surface; pagos and
// eventos follow.
export const BACKOFFICE_NAV_GROUPS = [
  {
    label: 'Plataforma',
    short: 'PLAT',
    items: [
      {
        label: 'WhatsApp',
        path: '/plataforma/whatsapp',
        shortcut: 'W',
        Icon: InboxOutlinedIcon,
      },
      {
        label: 'Pagos',
        path: '/plataforma/pagos',
        shortcut: 'P',
        Icon: PaymentsOutlinedIcon,
      },
      {
        label: 'Eventos',
        path: '/plataforma/eventos',
        shortcut: 'E',
        Icon: SwapIcon,
      },
      {
        label: 'Documentos',
        path: '/plataforma/documentos',
        shortcut: 'D',
        Icon: DescriptionIcon,
        perm: 'payout:manual',
      },
      {
        label: 'Configuración',
        path: '/plataforma/configuracion',
        shortcut: 'C',
        Icon: GearIcon,
        perm: 'platform-settings:read',
      },
    ],
  },
];

// Per-capability nav resolver (mirrors the prototype's CAP map). Backoffice gets
// its own platform nav; firm orgs (solo/enterprise) share the firm nav.
// Receptores use ReceptorLayout, not this rail.
export function resolveNavGroups(
  capability,
  {
    canReadBilling = false,
    canManualPayout = false,
    canReadPlatformSettings = false,
  } = {},
) {
  if (capability === 'backoffice') {
    return BACKOFFICE_NAV_GROUPS.map((g) => ({
      ...g,
      items: g.items.filter(
        (it) =>
          (it.perm !== 'payout:manual' || canManualPayout) &&
          (it.perm !== 'platform-settings:read' || canReadPlatformSettings),
      ),
    }));
  }
  return NAV_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((it) => it.perm !== 'billing' || canReadBilling),
  })).filter((g) => g.items.length > 0);
}

// Account menu — the placement is the SIDEBAR FOOTER
// ("Cuenta + estudio · menú inferior · avatar"), not the topbar. The trigger is
// styled to the `.rail__acct` button; the popup reuses the shared
// DropdownMenu (org header / org switcher / identity / Configuración / logout),
// opening upward from the footer. `account` is built in MainLayout from useAuth.
function SidebarAccount({ account, collapsed }) {
  const [open, setOpen] = useState(false);
  // orgView: when true, the menu panel shows the drill-in org list instead of
  // the main menu. Resets to false every time the menu closes.
  const [orgView, setOrgView] = useState(false);
  const wrapRef = useRef(null);

  function closeMenu() {
    setOpen(false);
    setOrgView(false);
  }

  useEffect(() => {
    if (!open) return undefined;
    function onDocClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        closeMenu();
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
    // Effect intentionally re-runs only on `open` changes.
  }, [open]);

  if (!account) return null;

  // Map main-view items: detect `orgSwitch` items and wire them to drill into
  // the org sub-view (keepOpen so the menu stays open, onSelect = setOrgView).
  const mappedMenuItems = Array.isArray(account.menuItems)
    ? account.menuItems.map((item) => {
        if (item.type === 'item' && item.orgSwitch) {
          return {
            ...item,
            keepOpen: true,
            onSelect: () => setOrgView(true),
          };
        }
        if (item.type === 'item') {
          return {
            ...item,
            onSelect: () => {
              item.onSelect?.();
              closeMenu();
            },
          };
        }
        return item;
      })
    : [];

  return (
    <div
      ref={wrapRef}
      className={`sidebar-b__acct${open ? ' sidebar-b__acct--open' : ''}`}
    >
      <button
        type="button"
        className="sidebar-b__acct-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        title={
          collapsed
            ? `${account.name} · ${account.orgName}`
            : 'Cuenta y organización'
        }
        onClick={() => setOpen((v) => !v)}
      >
        <span className="sidebar-b__acct-av" aria-hidden="true">
          {account.initials}
        </span>
        {!collapsed && (
          <span className="sidebar-b__acct-info">
            <span className="sidebar-b__acct-name">{account.name}</span>
            <span className="sidebar-b__acct-studio">{account.orgName}</span>
          </span>
        )}
        {!collapsed && (
          <span className="sidebar-b__acct-caret" aria-hidden="true">
            ▾
          </span>
        )}
      </button>

      {open && (
        <div className="sidebar-b__acct-menu">
          {!orgView ? (
            <DropdownMenu items={mappedMenuItems} onClose={closeMenu} />
          ) : (
            // Org sub-view panel — same .dd-menu shell as DropdownMenu.
            <div className="dd-menu" role="menu">
              {/* Back header */}
              <button
                type="button"
                className="dd-menu__item dd-menu__item--back"
                role="menuitem"
                tabIndex={-1}
                onClick={() => setOrgView(false)}
              >
                <span className="dd-menu__ico">
                  <ChevronLeftIcon size={14} />
                </span>
                <span>Cambiar de organización</span>
              </button>
              <div className="dd-menu__sep" role="separator" />

              {/* Org rows */}
              {Array.isArray(account.orgs) &&
                account.orgs.map((org) => (
                  <button
                    key={org.id}
                    type="button"
                    className={`dd-menu__item dd-menu__item--org${org.active ? ' dd-menu__item--org-active' : ''}`}
                    role="menuitem"
                    tabIndex={-1}
                    onClick={() => {
                      if (!org.active && account.onSwitchOrg) {
                        account.onSwitchOrg(org.id);
                      }
                      closeMenu();
                    }}
                  >
                    <span
                      className="dd-menu__org-av"
                      style={{ background: org.color }}
                      aria-hidden="true"
                    >
                      {org.initials}
                    </span>
                    <span className="dd-menu__org-info">
                      <span className="dd-menu__org-name">{org.name}</span>
                      {org.meta && (
                        <span className="dd-menu__org-meta">{org.meta}</span>
                      )}
                    </span>
                    {org.active ? (
                      <span
                        className="dd-menu__org-check"
                        aria-label="Organización activa"
                      >
                        ✓
                      </span>
                    ) : (
                      <span className="dd-menu__org-arrow" aria-hidden="true">
                        <ChevronRightIcon size={13} />
                      </span>
                    )}
                  </button>
                ))}

              <div className="dd-menu__sep" role="separator" />

              {/* Muted note */}
              <p className="dd-menu__org-note">
                Cambia entre las organizaciones a las que tienes acceso. El
                backoffice es la plataforma interna de Recepthor.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Sidebar({
  activeItem,
  onNavigate,
  collapsed = false,
  onToggleCollapse,
  showCollapseToggle = true,
  orgLabel,
  groups = NAV_GROUPS,
  account = null,
}) {
  const rootCls = ['sidebar-b', collapsed && 'sidebar-b--collapsed']
    .filter(Boolean)
    .join(' ');

  return (
    <aside className={rootCls}>
      <div className="sidebar-b__brand">
        <img className="sidebar-b__brand-hammer" src={hammer} alt="Recepthor" />
        {!collapsed && (
          <span className="sidebar-b__wm">
            <span className="sidebar-b__wm-a">RECEP</span>
            <span className="sidebar-b__wm-b">THOR</span>
          </span>
        )}
        {/* The visible control for the `[` shortcut. Hidden under 900px, where
            MainLayout forces the rail collapsed and there is nothing to toggle. */}
        {showCollapseToggle && onToggleCollapse && (
          <button
            type="button"
            className="sidebar-b__collapse-btn"
            aria-label={collapsed ? 'Expandir menú' : 'Colapsar menú'}
            aria-expanded={!collapsed}
            onClick={onToggleCollapse}
          >
            {collapsed ? (
              <ChevronRightIcon size={16} />
            ) : (
              <ChevronLeftIcon size={16} />
            )}
          </button>
        )}
      </div>
      <div className="sidebar-b__brand-rule" />

      {groups.map((group) => (
        <nav
          key={group.label}
          className="sidebar-b__nav-section"
          aria-label={group.label}
        >
          <div className="sidebar-b__nav-section-label">
            {collapsed ? group.short : group.label}
          </div>
          {group.items.map((item) => {
            const isActive = activeItem
              ? activeItem === item.path
              : item.active;
            const cls = [
              'sidebar-b__nav-item',
              isActive && 'sidebar-b__nav-item--active',
            ]
              .filter(Boolean)
              .join(' ');
            const Icon = item.Icon;
            // The org area renames itself to the active org's kind.
            const label =
              orgLabel && item.path === '/sistema/organizacion'
                ? orgLabel
                : item.label;
            const navigable = onNavigate;
            const collapsedTitle = item.shortcut
              ? `${label}  ·  G ${item.shortcut}`
              : label;

            return (
              <div
                key={item.path}
                className={cls}
                title={collapsed ? collapsedTitle : undefined}
                onClick={navigable ? () => onNavigate(item.path) : undefined}
                role="button"
                tabIndex={0}
                onKeyDown={
                  navigable
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onNavigate(item.path);
                        }
                      }
                    : undefined
                }
              >
                <span className="sidebar-b__nav-item-main">
                  {Icon && <Icon className="sidebar-b__icon" size={18} />}
                  {!collapsed && <span>{label}</span>}
                  {!collapsed && item.badge && (
                    <span
                      className={`sidebar-b__badge${
                        item.badgeVariant
                          ? ` sidebar-b__badge--${item.badgeVariant}`
                          : ''
                      }`}
                    >
                      {item.badge}
                    </span>
                  )}
                </span>
                {/* Trailing group, pushed right by space-between: the active
                    count and the g-chord. */}
                {!collapsed && (item.count != null || item.shortcut) && (
                  <span className="sidebar-b__nav-item-end">
                    {item.count != null && (
                      <span className="sidebar-b__count" aria-hidden="true">
                        {item.count}
                      </span>
                    )}
                    {item.shortcut && (
                      <kbd className="sidebar-b__kbd" aria-hidden="true">
                        G {item.shortcut}
                      </kbd>
                    )}
                  </span>
                )}
              </div>
            );
          })}
        </nav>
      ))}

      <div className="sidebar-b__footer">
        <SidebarAccount account={account} collapsed={collapsed} />
      </div>
    </aside>
  );
}
